import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { GraduationCap, Plus, Clock, Calendar, Save } from "lucide-react";
import { toast } from "sonner";
import { formatUKDate } from "@/lib/dateUtils";

interface SupervisionNotesProps {
  clinicianId: string;
  clientId?: string;
}

export function SupervisionNotes({ clinicianId, clientId }: SupervisionNotesProps) {
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newNote, setNewNote] = useState({
    supervisor_name: '',
    supervision_date: new Date().toISOString().split('T')[0],
    case_presentation: '',
    supervisor_feedback: '',
    action_points: [''],
    ethical_considerations: '',
    countertransference_notes: '',
    cpd_hours: ''
  });

  // Fetch notes
  const { data: notes, isLoading } = useQuery({
    queryKey: ['supervision-notes', clinicianId, clientId],
    queryFn: async () => {
      let query = supabase
        .from('supervision_notes')
        .select('*')
        .eq('clinician_id', clinicianId)
        .order('supervision_date', { ascending: false });
      
      if (clientId) {
        query = query.eq('client_id', clientId);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data;
    }
  });

  // Save note
  const saveMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('supervision_notes')
        .insert({
          clinician_id: clinicianId,
          client_id: clientId || null,
          supervisor_name: newNote.supervisor_name,
          supervision_date: newNote.supervision_date,
          case_presentation: newNote.case_presentation,
          supervisor_feedback: newNote.supervisor_feedback,
          action_points: newNote.action_points.filter(a => a.trim()),
          ethical_considerations: newNote.ethical_considerations || null,
          countertransference_notes: newNote.countertransference_notes || null,
          cpd_hours: newNote.cpd_hours ? parseFloat(newNote.cpd_hours) : null
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supervision-notes', clinicianId, clientId] });
      setShowAddForm(false);
      setNewNote({
        supervisor_name: '',
        supervision_date: new Date().toISOString().split('T')[0],
        case_presentation: '',
        supervisor_feedback: '',
        action_points: [''],
        ethical_considerations: '',
        countertransference_notes: '',
        cpd_hours: ''
      });
      toast.success("Supervision notes saved");
    },
    onError: () => {
      toast.error("Failed to save notes");
    }
  });

  const addActionPoint = () => {
    setNewNote({
      ...newNote,
      action_points: [...newNote.action_points, '']
    });
  };

  const updateActionPoint = (index: number, value: string) => {
    const newPoints = [...newNote.action_points];
    newPoints[index] = value;
    setNewNote({ ...newNote, action_points: newPoints });
  };

  // Calculate total CPD hours
  const totalCpdHours = notes?.reduce((sum, note) => sum + (note.cpd_hours || 0), 0) || 0;

  return (
    <Card className="bg-card/50 backdrop-blur-sm border-border/30">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-primary" />
              Supervision Notes
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Confidential clinical supervision records
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="text-xs">
              <Clock className="h-3 w-3 mr-1" />
              {totalCpdHours.toFixed(1)} CPD Hours
            </Badge>
            <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Notes
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Supervision Session Notes</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Supervisor Name</label>
                      <Input
                        value={newNote.supervisor_name}
                        onChange={(e) => setNewNote({ ...newNote, supervisor_name: e.target.value })}
                        placeholder="Dr. Jane Smith"
                        className="bg-background/50"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Date</label>
                      <Input
                        type="date"
                        value={newNote.supervision_date}
                        onChange={(e) => setNewNote({ ...newNote, supervision_date: e.target.value })}
                        className="bg-background/50"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Case Presentation</label>
                    <Textarea
                      value={newNote.case_presentation}
                      onChange={(e) => setNewNote({ ...newNote, case_presentation: e.target.value })}
                      placeholder="Summary of case presented for supervision..."
                      className="bg-background/50 min-h-24"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Supervisor Feedback</label>
                    <Textarea
                      value={newNote.supervisor_feedback}
                      onChange={(e) => setNewNote({ ...newNote, supervisor_feedback: e.target.value })}
                      placeholder="Key feedback and recommendations from supervisor..."
                      className="bg-background/50 min-h-24"
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium">Action Points</label>
                      <Button size="sm" variant="ghost" onClick={addActionPoint}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {newNote.action_points.map((point, idx) => (
                      <Input
                        key={idx}
                        value={point}
                        onChange={(e) => updateActionPoint(idx, e.target.value)}
                        placeholder={`Action ${idx + 1}`}
                        className="bg-background/50"
                      />
                    ))}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Ethical Considerations</label>
                    <Textarea
                      value={newNote.ethical_considerations}
                      onChange={(e) => setNewNote({ ...newNote, ethical_considerations: e.target.value })}
                      placeholder="Any ethical issues discussed..."
                      className="bg-background/50"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Countertransference Notes</label>
                    <Textarea
                      value={newNote.countertransference_notes}
                      onChange={(e) => setNewNote({ ...newNote, countertransference_notes: e.target.value })}
                      placeholder="Personal reactions and countertransference explored..."
                      className="bg-background/50"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">CPD Hours</label>
                    <Input
                      type="number"
                      step="0.5"
                      value={newNote.cpd_hours}
                      onChange={(e) => setNewNote({ ...newNote, cpd_hours: e.target.value })}
                      placeholder="1.5"
                      className="bg-background/50 w-32"
                    />
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" onClick={() => setShowAddForm(false)}>
                      Cancel
                    </Button>
                    <Button
                      onClick={() => saveMutation.mutate()}
                      disabled={!newNote.supervisor_name || saveMutation.isPending}
                    >
                      <Save className="h-4 w-4 mr-1" />
                      Save Notes
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <p className="text-muted-foreground text-sm">Loading notes...</p>
        ) : notes?.length === 0 ? (
          <div className="text-center py-8">
            <GraduationCap className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground">No supervision notes recorded</p>
            <p className="text-sm text-muted-foreground">
              Document your clinical supervision sessions here
            </p>
          </div>
        ) : (
          notes?.map(note => (
            <div
              key={note.id}
              className="p-4 rounded-lg bg-muted/30 border border-border/30 space-y-3"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    {formatUKDate(new Date(note.supervision_date))}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    with {note.supervisor_name}
                  </span>
                </div>
                {note.cpd_hours && (
                  <Badge variant="outline" className="text-xs">
                    {note.cpd_hours} CPD hrs
                  </Badge>
                )}
              </div>

              {note.case_presentation && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Case Presentation</p>
                  <p className="text-sm">{note.case_presentation}</p>
                </div>
              )}

              {note.supervisor_feedback && (
                <div className="p-3 rounded bg-primary/10 border border-primary/20">
                  <p className="text-xs font-medium text-primary mb-1">Supervisor Feedback</p>
                  <p className="text-sm">{note.supervisor_feedback}</p>
                </div>
              )}

              {note.action_points?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Action Points</p>
                  <ul className="list-disc list-inside text-sm">
                    {note.action_points.map((point: string, idx: number) => (
                      <li key={idx}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}

              {note.countertransference_notes && (
                <div className="p-3 rounded bg-accent/20 border border-accent/30">
                  <p className="text-xs font-medium mb-1">Countertransference</p>
                  <p className="text-sm text-muted-foreground">{note.countertransference_notes}</p>
                </div>
              )}
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
